import { useState, useEffect } from 'react';
import { Shield, ChevronLeft, ChevronRight, AlertCircle, Clock, User, Filter } from 'lucide-react';
import api from '../../services/api';

const actionLabels = {
  'role_changed': 'Role Changed',
  'user_deactivated': 'User Deactivated',
  'payment_completed': 'Payment Completed',
  'payment_failed': 'Payment Failed',
  'payment_method_added': 'Payment Method Added',
  'payment_method_deleted': 'Payment Method Deleted',
  'recurring_payment_created': 'Recurring Payment Created',
  'recurring_payment_cancelled': 'Recurring Payment Cancelled',
  'autopay_configured': 'AutoPay Configured',
  'autopay_disabled': 'AutoPay Disabled',
  'quote_session_created': 'Quote Session Created',
  'quote_accepted': 'Quote Accepted',
};

const actionColors = {
  'role_changed': 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300',
  'user_deactivated': 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
  'payment_completed': 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
  'payment_failed': 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
  'payment_method_added': 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  'payment_method_deleted': 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300',
  'recurring_payment_created': 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  'recurring_payment_cancelled': 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300',
  'autopay_configured': 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
  'autopay_disabled': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300',
  'quote_session_created': 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  'quote_accepted': 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
};

export default function AuditLogPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionFilter, setActionFilter] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });

  const fetchAuditLog = async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
      });

      if (actionFilter) params.append('action', actionFilter);

      const response = await api.get(`/admin/audit?${params}`);
      setEntries(response.data.entries);
      setPagination(response.data.pagination);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLog(1);
  }, [actionFilter]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatDetails = (details) => {
    if (!details || typeof details !== 'object') return null;

    // Special formatting for role changes
    if (details.oldRole && details.newRole) {
      return (
        <span>
          Changed role from <strong>{details.oldRole}</strong> to <strong>{details.newRole}</strong>
          {details.targetEmail && <> for {details.targetEmail}</>}
        </span>
      );
    }

    // Generic key-value display for other details
    const entries = Object.entries(details).filter(([key, value]) =>
      value !== null && value !== undefined && key !== 'changedBy'
    );

    if (entries.length === 0) return null;

    return (
      <span className="text-neutral-500">
        {entries.slice(0, 3).map(([key, value], idx) => (
          <span key={key}>
            {idx > 0 && ' | '}
            {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
          </span>
        ))}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">Audit Log</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Track system activities and changes
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
        <div className="flex items-center gap-4">
          <Filter className="w-4 h-4 text-neutral-400" />
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
          >
            <option value="">All Actions</option>
            {Object.entries(actionLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            {pagination.total} total entries
          </span>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3 text-red-700 dark:text-red-300">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Audit Log List */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-neutral-500 dark:text-neutral-400">
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              Loading audit log...
            </div>
          </div>
        ) : entries.length === 0 ? (
          <div className="p-12 text-center text-neutral-500 dark:text-neutral-400">
            <Shield className="w-8 h-8 mx-auto mb-2 text-neutral-400 dark:text-neutral-500" />
            No audit entries found
          </div>
        ) : (
          <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
            {entries.map((entry) => (
              <div key={entry.id} className="p-4 hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-neutral-600 dark:text-neutral-300" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                        actionColors[entry.action] || 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300'
                      }`}>
                        {actionLabels[entry.action] || entry.action}
                      </span>
                      <span className="text-sm text-neutral-500 dark:text-neutral-400">
                        {entry.entity_type && `on ${entry.entity_type}`}
                        {entry.entity_id && ` #${entry.entity_id.substring(0, 8)}...`}
                      </span>
                    </div>

                    {/* User info */}
                    <div className="flex items-center gap-2 text-sm">
                      <User className="w-4 h-4 text-neutral-400" />
                      <span className="text-neutral-700 dark:text-neutral-300">
                        {entry.user_email || 'System'}
                      </span>
                      <span className="text-neutral-400">|</span>
                      <Clock className="w-4 h-4 text-neutral-400" />
                      <span className="text-neutral-500 dark:text-neutral-400">
                        {formatDate(entry.created_at)}
                      </span>
                      {entry.ip_address && (
                        <>
                          <span className="text-neutral-400">|</span>
                          <span className="text-neutral-500 dark:text-neutral-400">
                            IP: {entry.ip_address}
                          </span>
                        </>
                      )}
                    </div>

                    {/* Details */}
                    {entry.details && (
                      <div className="mt-2 text-sm">
                        {formatDetails(entry.details)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} entries
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchAuditLog(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-neutral-300"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-neutral-600 dark:text-neutral-400">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => fetchAuditLog(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-neutral-300"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
